import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  backfillInvoice,
  type BackfillInvoiceResponse,
} from '../../lib/ops-backfill-api';
import { isShopAccessRequired, opsErrorMessage } from '../../lib/ops-api';
import { readBackfillOrder } from '../../lib/shop-file';
import { PageBody } from '../../components/Page';
import { StatusBanner } from '../../components/StatusBanner';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useShopFile } from '../../components/ShopFileLayout';
import { useLocale } from '../../i18n/LocaleProvider';

interface FieldErrors {
  orderId?: string;
  invoiceNumberOverride?: string;
  reason?: string;
}

/**
 * One-order backfill: create the missing invoice for a Shopify order whose webhook was lost.
 * Submitting POSTs to /internal-admin/shops/:shopId/invoices/backfill (behind the file's access
 * grant), which fetches the order from Shopify,
 * runs the standard invoice creation pipeline, and writes an OperatorAuditLog row
 * (Verfahrensdokumentation).
 *
 * The shop is the file's — there is no domain to type, and so no way to backfill onto the wrong
 * shop by a typo. `?order=` prefills the order: the order diagnosis links here with the order it
 * found un-invoiced. Only prefilled: the reason and the confirmation are still the operator's.
 */
export function BackfillTab() {
  const { t, tNode } = useLocale();
  const { shopId, file, reload, requireAccess } = useShopFile();
  const shopDomain = file.identity.shopDomain;
  const [searchParams] = useSearchParams();
  const [prefilledOrder] = useState(() => readBackfillOrder(searchParams));
  const [orderId, setOrderId] = useState(prefilledOrder);
  const [invoiceNumberOverride, setInvoiceNumberOverride] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [result, setResult] = useState<BackfillInvoiceResponse | null>(null);

  function validate(): FieldErrors {
    const errs: FieldErrors = {};
    if (!/^\d+$/.test(orderId)) {
      errs.orderId = t('backfill.errorOrderId');
    }
    if (invoiceNumberOverride !== '') {
      const n = Number(invoiceNumberOverride);
      if (!Number.isInteger(n) || n <= 0) {
        errs.invoiceNumberOverride = t('backfill.errorInvoiceNumber');
      }
    }
    if (reason.trim().length < 10) {
      errs.reason = t('common.reasonMin');
    }
    if (reason.length > 500) {
      errs.reason = t('common.reasonMax');
    }
    return errs;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setResult(null);
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setConfirming(true);
  }

  async function performBackfill() {
    setConfirming(false);
    setLoading(true);
    setError('');
    try {
      const response = await backfillInvoice(shopId, {
        orderId: orderId.trim(),
        invoiceNumberOverride:
          invoiceNumberOverride === ''
            ? undefined
            : Number(invoiceNumberOverride),
        reason: reason.trim(),
      });
      setResult(response);
      setOrderId('');
      setInvoiceNumberOverride('');
      setReason('');
      // The file's header and overview count documents and read the counter this just moved.
      reload();
    } catch (err) {
      if (isShopAccessRequired(err, shopId)) {
        requireAccess();
        return;
      }
      setError(opsErrorMessage(err, t('backfill.failed')));
    } finally {
      setLoading(false);
    }
  }

  return (
    <PageBody narrow>
      <section className="card space-y-5">
        <div className="space-y-2">
          <h2 className="text-base font-semibold text-gray-900">
            {t('backfill.title')}
          </h2>
          <p className="text-sm text-gray-600">
            {tNode('backfill.lead', {
              auditLog: <code className="text-gray-900">OperatorAuditLog</code>,
            })}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="orderId" className="field-label">
              {t('backfill.orderId')}
            </label>
            <input
              id="orderId"
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="8554144661768"
              autoComplete="off"
              className="field-input font-mono"
            />
            {fieldErrors.orderId && (
              <p className="field-error">{fieldErrors.orderId}</p>
            )}
            <p className="field-hint">{t('backfill.orderIdHint')}</p>
            {prefilledOrder !== '' && orderId === prefilledOrder && (
              <p className="field-hint">{t('diagnosis.backfillPrefilled')}</p>
            )}
          </div>

          <div>
            <label htmlFor="invoiceNumberOverride" className="field-label">
              {t('backfill.invoiceNumber')}
            </label>
            <input
              id="invoiceNumberOverride"
              type="number"
              min="1"
              step="1"
              value={invoiceNumberOverride}
              onChange={(e) => setInvoiceNumberOverride(e.target.value)}
              placeholder={t('backfill.invoiceNumberPlaceholder')}
              autoComplete="off"
              className="field-input font-mono"
            />
            {fieldErrors.invoiceNumberOverride && (
              <p className="field-error">{fieldErrors.invoiceNumberOverride}</p>
            )}
            <p className="field-hint">
              {tNode('backfill.invoiceNumberHint', {
                field: (
                  <code className="text-gray-700">Shop.currentInvoiceNo</code>
                ),
              })}
            </p>
          </div>

          <div>
            <label htmlFor="reason" className="field-label">
              {t('backfill.reason')}
            </label>
            <textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder={t('backfill.reasonPlaceholder')}
              className="field-input"
            />
            <div className="flex justify-between items-center gap-4">
              {fieldErrors.reason ? (
                <p className="field-error">{fieldErrors.reason}</p>
              ) : (
                <p className="field-hint">{t('backfill.reasonHint')}</p>
              )}
              <p className="field-hint tabular-nums shrink-0">
                {reason.length}/500
              </p>
            </div>
          </div>

          {error && <StatusBanner tone="error">{error}</StatusBanner>}

          {result && (
            <StatusBanner
              tone={result.pdfQueueStatus === 'failed' ? 'warning' : 'success'}
            >
              <p className="font-semibold">
                {t(
                  result.pdfQueueStatus === 'failed'
                    ? 'backfill.resultPdfFailed'
                    : 'backfill.resultSuccess',
                  { number: result.formattedInvoiceNumber },
                )}
              </p>
              <p className="text-xs font-mono break-all opacity-80 mt-1">
                invoiceId: {result.invoiceId}
                {!result.isNew && ` ${t('backfill.resultExisting')}`}
              </p>
              <p className="text-xs opacity-80 mt-1">
                {t('backfill.resultIssueDate')}{' '}
                <span className="font-mono">{result.issueDateUsed}</span>{' '}
                <span className="opacity-70">
                  {t('backfill.resultFromProcessedAt', {
                    value: result.shopifyOrderProcessedAt,
                  })}
                </span>
              </p>
              <p className="text-xs opacity-80 mt-1">
                {t('backfill.resultPdfQueue')}{' '}
                <span className="font-mono">{result.pdfQueueStatus}</span>
                {result.pdfQueueStatus === 'failed' && (
                  <span className="ml-2 font-medium">
                    {t('backfill.resultRequeueHint')}
                  </span>
                )}
              </p>
            </StatusBanner>
          )}

          <button
            type="submit"
            disabled={loading}
            className="btn-danger btn-sm w-full"
          >
            {loading ? t('backfill.submitting') : t('backfill.submit')}
          </button>
        </form>
      </section>

      <ConfirmDialog
        open={confirming}
        title={t('backfill.confirmTitle')}
        confirmLabel={t('backfill.confirmLabel')}
        onConfirm={performBackfill}
        onCancel={() => setConfirming(false)}
      >
        {/*
          One whole sentence per variant rather than an optional clause spliced in: German puts
          the number before the verb, so an English-ordered "…on {shop}" + "with number {n}" +
          "." would read as broken German however the pieces were translated.
        */}
        {tNode(
          invoiceNumberOverride
            ? 'backfill.confirmBodyWithNumber'
            : 'backfill.confirmBody',
          {
            order: <code className="text-gray-900">{orderId}</code>,
            shop: <code className="text-gray-900">{shopDomain}</code>,
            number: (
              <code className="text-gray-900">{invoiceNumberOverride}</code>
            ),
          },
        )}
      </ConfirmDialog>
    </PageBody>
  );
}
